// src/pages/GuildIntro.js - 길드 소개 페이지 (디자인 개선)
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from "firebase/firestore";
// 기존 import에 추가
import GuildLogo from "./GuildLogo";
import GuildHashtags from "./GuildHashtags";
function GuildIntro() {
  const { guildId } = useParams();
  const navigate = useNavigate();
  const localUser = JSON.parse(localStorage.getItem("user")) || {};
  
  const [guild, setGuild] = useState(null);
  const [members, setMembers] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (guildId) {
      loadGuildData();
    }
  }, [guildId]);

  const loadGuildData = async () => {
    try {
      setLoading(true);
      
      // GuildDataService를 사용하여 길드 정보 로드 (Guild.js와 동일한 방식)
      const guildDoc = await getDoc(doc(db, "guilds", guildId));
      if (!guildDoc.exists()) {
        alert("존재하지 않는 길드입니다.");
        navigate("/ranking");
        return;
      }
      
      const guildData = { id: guildDoc.id, ...guildDoc.data() };
      setGuild(guildData);
      
      // 길드 멤버 로드 (GuildDataService와 호환되는 방식)
      const membersQuery = query(
        collection(db, "guildMembers"),
        where("guildId", "==", guildId)
      );
      const membersSnap = await getDocs(membersQuery);
      
      const membersData = [];
      for (const memberDoc of membersSnap.docs) {
        const memberData = memberDoc.data();
        
        // 사용자 정보 가져오기
        const userQuery = query(
          collection(db, "users"),
          where("id", "==", memberData.userId)
        );
        const userSnap = await getDocs(userQuery);
        
        if (!userSnap.empty) {
          const userData = userSnap.docs[0].data();
          membersData.push({
            id: memberData.userId, // Guild.js와 호환
            ...memberData,
            nickname: userData.nickname || memberData.nickname || userData.id,
            profileImg: userData.profileImg || '',
            mmr: userData.mmr || 0,
            lastLogin: userData.lastLoginDate?.toDate() || userData.lastLogin?.toDate() || new Date(),
            // Guild.js와 호환되도록 필드명 통일
            guildTier: memberData.role || memberData.tier || "임시회원",
            guildRole: memberData.guildRole || "",
            contribution: memberData.contribution || 0,
            guildWins: memberData.guildWins || 0,
            guildLosses: memberData.guildLosses || 0,
            joinedGuildAt: memberData.joinDate?.toDate() || memberData.joinedGuildAt?.toDate() || new Date()
          });
        }
      }
      
      // 멤버를 역할별로 정렬 (길드장 > 부길드장 > 정회원 > 임시회원)
      const roleOrder = { "길드장": 0, "부길드장": 1, "정회원": 2, "임시회원": 3 };
      membersData.sort((a, b) => {
        const aOrder = roleOrder[a.guildTier] || 4;
        const bOrder = roleOrder[b.guildTier] || 4;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return b.mmr - a.mmr; // MMR 높은 순
      });
      
      setMembers(membersData);
      
      // 최근 경기 기록 로드
      try {
        const matchesQuery = query(
          collection(db, "guildMatches"),
          where("guildId", "==", guildId),
          orderBy("createdAt", "desc"),
          limit(5)
        );
        const matchesSnap = await getDocs(matchesQuery);
        const matchesData = matchesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setRecentMatches(matchesData);
      } catch (matchError) {
        console.log("경기 기록 로드 실패 (무시):", matchError);
        setRecentMatches([]);
      }
      
    } catch (error) {
      console.error("길드 데이터 로드 실패:", error);
      alert("길드 정보를 불러올 수 없습니다.");
      navigate("/ranking");
    } finally {
      setLoading(false);
    }
  };

  const getTierIcon = (mmr) => {
    if (mmr >= 2000) return "🔥";
    if (mmr >= 1800) return "💎";
    if (mmr >= 1600) return "🏆";
    if (mmr >= 1400) return "🥇";
    if (mmr >= 1200) return "🥈";
    return "🥉";
  };

  const getRoleColor = (guildTier) => {
    switch(guildTier) {
      case "길드장": return "#fbbf24";
      case "부길드장": return "#8b5cf6";
      case "정회원": return "#10b981";
      case "임시회원": return "#6b7280";
      default: return "#6b7280";
    }
  };

  const getOnlineStatus = (lastLogin) => {
    const now = new Date();
    const diff = now - lastLogin;
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 5) return { status: "온라인", color: "#10b981" };
    if (minutes < 60) return { status: `${minutes}분 전`, color: "#f59e0b" };
    if (minutes < 1440) return { status: `${Math.floor(minutes/60)}시간 전`, color: "#ef4444" };
    return { status: "오프라인", color: "#6b7280" };
  };

  if (loading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        fontSize: 18,
        fontWeight: 600
      }}>
        <div style={{
          textAlign: "center"
        }}>
          <div style={{
            width: 50,
            height: 50,
            border: "4px solid rgba(255,255,255,0.3)",
            borderTop: "4px solid #fff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 20px"
          }}></div>
          길드 정보를 불러오는 중...
        </div>
      </div>
    );
  }

  if (!guild) return null;

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      padding: "20px 0"
    }}>
      <div style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: "0 20px"
      }}>
        {/* 헤더 */}
        <div style={{
          background: "rgba(255,255,255,0.1)",
          backdropFilter: "blur(20px)",
          borderRadius: 24,
          padding: 40,
          marginBottom: 24,
          color: "#fff",
          textAlign: "center"
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 24,
            marginBottom: 20
          }}>
            <div style={{
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: guild.logoUrl ? 
                `url(${guild.logoUrl})` : 
                "linear-gradient(135deg, #fbbf24, #f59e0b)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 48,
              fontWeight: 700,
              border: "4px solid rgba(255,255,255,0.3)"
            }}>
              {!guild.logoUrl && "🏰"}
            </div>
            <div style={{ textAlign: "left" }}>
              <h1 style={{
                fontSize: 42,
                fontWeight: 900,
                margin: "0 0 8px 0",
                textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
              }}>
                {guild.name}
              </h1>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                fontSize: 16,
                opacity: 0.9
              }}>
                <span style={{
                  background: "rgba(255,255,255,0.2)",
                  padding: "4px 12px",
                  borderRadius: 20,
                  fontWeight: 600
                }}>
                  🎯 {guild.sport}
                </span>
                <span>📍 {guild.region}</span>
                <span>👥 {members.length}명</span>
                <span>🏆 {guild.point || 0}점</span>
              </div>
            </div>
          </div>
          
          <p style={{
            fontSize: 18,
            margin: 0,
            opacity: 0.9,
            lineHeight: 1.6
          }}>
            {guild.intro || guild.description || "길드 소개글이 없습니다."}
          </p>
        </div>

        {/* 해시태그 표시 */}
        {guild.hashtags && guild.hashtags.length > 0 && (
          <div style={{ margin: '16px 0 0 0', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <GuildHashtags hashtags={guild.hashtags} size="medium" colorScheme="gradient" />
          </div>
        )}

        {/* 탭 메뉴 */}
        <div style={{
          display: "flex",
          gap: 8,
          marginBottom: 24,
          background: "rgba(255,255,255,0.1)",
          backdropFilter: "blur(20px)",
          borderRadius: 16,
          padding: 8
        }}>
          {["overview", "members", "matches"].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                background: activeTab === tab ? 
                  "rgba(255,255,255,0.9)" : "transparent",
                color: activeTab === tab ? "#374151" : "#fff",
                border: "none",
                borderRadius: 12,
                padding: "12px 20px",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.3s ease"
              }}
            >
              {tab === "overview" ? "📊 개요" :
               tab === "members" ? "👥 멤버" : "⚔️ 경기기록"}
            </button>
          ))}
        </div>

        {/* 콘텐츠 영역 */}
        <div style={{
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: 24,
          padding: 32,
          minHeight: 500
        }}>
          {/* 개요 탭 */}
          {activeTab === "overview" && (
            <div>
              <h3 style={{
                fontSize: 24,
                fontWeight: 800,
                color: "#1f2937",
                marginBottom: 24,
                display: "flex",
                alignItems: "center",
                gap: 12
              }}>
                📊 길드 개요
              </h3>
              
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: 24,
                marginBottom: 32
              }}>
                <div style={{
                  background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
                  color: "#fff",
                  padding: 24,
                  borderRadius: 16,
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>👑</div>
                  <div style={{ fontSize: 14, opacity: 0.9 }}>길드장</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    {guild.leaderName || "미설정"}
                  </div>
                </div>
                
                <div style={{
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "#fff",
                  padding: 24,
                  borderRadius: 16,
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🏆</div>
                  <div style={{ fontSize: 14, opacity: 0.9 }}>길드 점수</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    {guild.point || 0}점
                  </div>
                </div>
                
                <div style={{
                  background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                  color: "#fff",
                  padding: 24,
                  borderRadius: 16,
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📈</div>
                  <div style={{ fontSize: 14, opacity: 0.9 }}>평균 MMR</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    {members.length > 0 ? 
                      Math.round(members.reduce((sum, m) => sum + m.mmr, 0) / members.length) : 0}
                  </div>
                </div>
                
                <div style={{
                  background: "linear-gradient(135deg, #ef4444, #dc2626)",
                  color: "#fff",
                  padding: 24,
                  borderRadius: 16,
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>⚔️</div>
                  <div style={{ fontSize: 14, opacity: 0.9 }}>최근 경기</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    {recentMatches.length}건
                  </div>
                </div>
              </div>

              <div style={{
                background: "#f8fafc",
                padding: 24,
                borderRadius: 16,
                border: "1px solid #e2e8f0"
              }}>
                <h4 style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#374151",
                  marginBottom: 16
                }}>
                  🎯 길드 목표
                </h4>
                <p style={{
                  color: "#6b7280",
                  lineHeight: 1.6,
                  margin: 0
                }}>
                  {guild.description || "설정된 길드 목표가 없습니다."}
                </p>
              </div>
            </div>
          )}

          {/* 멤버 탭 */}
          {activeTab === "members" && (
            <div>
              <h3 style={{
                fontSize: 24,
                fontWeight: 800,
                color: "#1f2937",
                marginBottom: 24,
                display: "flex",
                alignItems: "center",
                gap: 12
              }}>
                👥 길드 멤버 ({members.length}명)
              </h3>
              
              <div style={{
                display: "grid",
                gap: 16
              }}>
                {members.map(member => {
                  const onlineStatus = getOnlineStatus(member.lastLogin);
                  return (
                    <div
                      key={member.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                        padding: 20,
                        background: "#fff",
                        borderRadius: 16,
                        border: "1px solid #e5e7eb",
                        cursor: "pointer",
                        transition: "all 0.2s ease"
                      }}
                      onClick={() => navigate(`/mypage/${member.userId || member.id}`)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <div style={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        background: member.profileImg ? 
                          `url(${member.profileImg})` : 
                          "linear-gradient(135deg, #667eea, #764ba2)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: 24,
                        fontWeight: 700,
                        position: "relative"
                      }}>
                        {!member.profileImg && "👤"}
                        <div style={{
                          position: "absolute",
                          bottom: -2,
                          right: -2,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background: onlineStatus.color,
                          border: "3px solid #fff"
                        }}></div>
                      </div>
                      
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          marginBottom: 4
                        }}>
                          <h4 style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: "#1f2937",
                            margin: 0
                          }}>
                            {member.nickname}
                          </h4>
                          <span style={{
                            background: getRoleColor(member.guildTier),
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: 12,
                            fontSize: 12,
                            fontWeight: 600
                          }}>
                            {member.guildTier}
                          </span>
                          {member.guildRole && (
                            <span style={{
                              background: "#f3f4f6",
                              color: "#374151",
                              padding: "2px 8px",
                              borderRadius: 12,
                              fontSize: 11,
                              fontWeight: 500
                            }}>
                              {member.guildRole}
                            </span>
                          )}
                        </div>
                        
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 16,
                          fontSize: 14,
                          color: "#6b7280"
                        }}>
                          <span>
                            {getTierIcon(member.mmr)} MMR {member.mmr}
                          </span>
                          <span style={{ color: onlineStatus.color }}>
                            ● {onlineStatus.status}
                          </span>
                          <span>
                            🎯 기여도 {member.contribution || 0}
                          </span>
                          <span>
                            🏆 {member.guildWins || 0}승 {member.guildLosses || 0}패
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {members.length === 0 && (
                <div style={{
                  textAlign: "center",
                  padding: 60,
                  color: "#9ca3af"
                }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>👥</div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>멤버가 없습니다</div>
                </div>
              )}
            </div>
          )}

          {/* 경기기록 탭 */}
          {activeTab === "matches" && (
            <div>
              <h3 style={{
                fontSize: 24,
                fontWeight: 800,
                color: "#1f2937",
                marginBottom: 24,
                display: "flex",
                alignItems: "center",
                gap: 12
              }}>
                ⚔️ 최근 경기 기록
              </h3>
              
              {recentMatches.length > 0 ? (
                <div style={{ display: "grid", gap: 16 }}>
                  {recentMatches.map(match => (
                    <div
                      key={match.id}
                      style={{
                        padding: 20,
                        background: "#fff",
                        borderRadius: 16,
                        border: "1px solid #e5e7eb"
                      }}
                    >
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 12
                      }}>
                        <div style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: "#374151"
                        }}>
                          vs {match.opponentName}
                        </div>
                        <div style={{
                          padding: "4px 12px",
                          borderRadius: 20,
                          fontSize: 14,
                          fontWeight: 600,
                          background: match.result === "승리" ? "#dcfce7" : 
                                    match.result === "패배" ? "#fef2f2" : "#fef3c7",
                          color: match.result === "승리" ? "#166534" : 
                                match.result === "패배" ? "#991b1b" : "#92400e"
                        }}>
                          {match.result}
                        </div>
                      </div>
                      
                      <div style={{
                        fontSize: 14,
                        color: "#6b7280"
                      }}>
                        {match.date && new Date(match.date.seconds * 1000).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: "center",
                  padding: 60,
                  color: "#9ca3af"
                }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>⚔️</div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>경기 기록이 없습니다</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 뒤로가기 버튼 */}
        <div style={{
          textAlign: "center",
          marginTop: 24
        }}>
          <button
            onClick={() => navigate("/ranking")}
            style={{
              background: "rgba(255,255,255,0.2)",
              color: "#fff",
              border: "2px solid rgba(255,255,255,0.3)",
              borderRadius: 16,
              padding: "12px 32px",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.3s ease",
              backdropFilter: "blur(10px)"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255,255,255,0.3)";
              e.target.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(255,255,255,0.2)";
              e.target.style.transform = "translateY(0)";
            }}
          >
            ← 랭킹으로 돌아가기
          </button>
        </div>
      </div>
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default GuildIntro;